import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import React from "react";
import { TrendsDataItem } from "./types";
import { ChartAxisTick } from "../PopulationPulse/chartAxis";

export interface FaceVoiceMonthlyTrendPoint extends TrendsDataItem {
    yearMonth?: string;
}

interface FaceVoiceScanMonthlyChartProps {
    trends?: FaceVoiceMonthlyTrendPoint[];
    seriesName?: string;
    tooltipName?: string;
    valueNoun?: string;
    emptyMessage?: string;
}

const tooltipStyle: React.CSSProperties = {
    borderRadius: 12,
    border: "1px solid rgba(0, 75, 77, 0.12)",
    boxShadow: "0 8px 24px -8px rgba(0, 75, 77, 0.15)",
};

function sortTrendPoints(points: FaceVoiceMonthlyTrendPoint[]): FaceVoiceMonthlyTrendPoint[] {
    return [...points].sort((a, b) => {
        if (a.yearMonth && b.yearMonth) {
            return a.yearMonth.localeCompare(b.yearMonth);
        }
        return 0;
    });
}

function yAxisMax(values: number[]): number {
    const peak = values.length > 0 ? Math.max(...values) : 0;
    if (peak <= 5) {
        return 5;
    }
    const padded = Math.ceil(peak * 1.15);
    return Math.max(padded, 1);
}

function LatestCountBadge({ count, noun }: { count: number; noun: string }): React.ReactElement {
    return (
        <div
            className={
                "absolute right-2 top-0 z-[1] rounded-md bg-portal-teal/90 px-3 py-1 " +
                "text-[13px] font-semibold tabular-nums text-pure-white shadow-sm ring-1 ring-portal-teal/30"
            }
            aria-label={`Latest month ${count} ${noun}`}
        >
            {count}
        </div>
    );
}

const FaceVoiceScanMonthlyChart = ({
    trends,
    seriesName = "Face and voice signals",
    tooltipName = "Face and voice signals",
    valueNoun = "signals",
    emptyMessage = "No face or voice signal history yet for this organisation.",
}: FaceVoiceScanMonthlyChartProps) => {
    const sortedData = React.useMemo(
        () => sortTrendPoints(trends ?? []).filter((p) => p.value > 0),
        [trends],
    );
    const nonZeroMonths = sortedData;
    const latestIndex = sortedData.length - 1;
    const latestCount =
        latestIndex >= 0 && sortedData[latestIndex].value > 0
            ? sortedData[latestIndex].value
            : nonZeroMonths.length > 0
              ? nonZeroMonths[nonZeroMonths.length - 1].value
              : null;
    const maxY = yAxisMax(sortedData.map((p) => p.value));

    if (sortedData.length === 0 || nonZeroMonths.length === 0) {
        return (
            <p className="mt-2 text-sm text-portal-teal/70">
                {emptyMessage}
            </p>
        );
    }

    return (
        <div className="relative mt-2 -mx-1 w-full min-w-0 sm:mx-0">
            {latestCount !== null && <LatestCountBadge count={latestCount} noun={valueNoun} />}
            <div className="h-[min(18rem,55vw)] w-full min-h-[220px] sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={sortedData}
                        margin={{ top: 16, right: 16, left: 4, bottom: 0 }}
                    >
                        <CartesianGrid
                            stroke="#e7edf8"
                            strokeDasharray="4 6"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="month"
                            tick={ChartAxisTick}
                            tickLine={false}
                            axisLine={{ stroke: "rgba(0,75,77,0.12)" }}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            allowDecimals={false}
                            domain={[0, maxY]}
                            tick={ChartAxisTick}
                            tickLine={false}
                            axisLine={false}
                            width={48}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelStyle={{
                                color: "#004b4d",
                                fontWeight: 600,
                                fontSize: 12,
                            }}
                            itemStyle={{ color: "#004b4d", fontSize: 13 }}
                            formatter={(value: number) => [`${value} ${valueNoun}`, tooltipName]}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            name={seriesName}
                            stroke="#0d9488"
                            strokeWidth={3}
                            dot={{ r: 4, fill: "#0f1f33" }}
                            activeDot={{
                                r: 6,
                                stroke: "#0f1f33",
                                strokeWidth: 2,
                                fill: "#fff",
                            }}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default FaceVoiceScanMonthlyChart;
